<template>
  <div class="product-form">
    <b-card>
      <template #header>
        <h2 class="mb-0">{{ isEdit ? $t('products.edit_title') : $t('products.create_title') }}</h2>
      </template>

      <b-form @submit.prevent="handleSubmit" :validated="validated" novalidate>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group id="name-group" :label="$t('products.form.name_label')" label-for="name"
              :state="!hasError('name')" :invalid-feedback="getError('name')">
              <b-form-input id="name" v-model="formFields.name" :state="!hasError('name')" required
                @input="removeError('name')"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="6">
            <b-form-group id="is_active-group" :label="$t('products.form.status_label')" label-for="is_active"
              :state="!hasError('is_active')" :invalid-feedback="getError('is_active')">
              <b-form-select id="is_active" v-model="formFields.is_active" :options="statusOptions"
                :state="!hasError('is_active')" required @input="removeError('is_active')"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="d-flex gap-2 mt-4">
          <b-button type="submit" variant="primary" :disabled="isSubmitting">
            <b-spinner small v-if="isSubmitting" class="mr-1"></b-spinner>
            {{ isSubmitting ? $t('common.saving') : $t('common.save') }}
          </b-button>
          <b-button variant="secondary" @click="cancel">{{ $t('common.cancel') }}</b-button>
        </div>

        <!-- Form Level Error Message -->
        <div v-if="formLevelError" class="mt-3">
          <b-alert show variant="danger" class="mb-0">
            {{ formLevelError }}
          </b-alert>
        </div>
      </b-form>
    </b-card>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import NotifyMixin from "@/Util/NotifyMixin";

export default {
  name: 'ProductForm',
  mixins: [NotifyMixin],
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      isSubmitting: false,
      validated: false,
      formErrors: {},
      formLevelError: '',
      formFields: {
        name: '',
        is_active: 1
      },
      statusOptions: [
        { value: 1, text: this.$t('products.status_active') },
        { value: 0, text: this.$t('products.status_inactive') }
      ]
    }
  },

  computed: {
    isEdit() {
      return !!this.id;
    }
  },

  methods: {
    hasError(field) {
      return !!this.formErrors[field];
    },

    getError(field) {
      return this.formErrors[field] ? this.formErrors[field][0] : '';
    },

    removeError(field) {
      if (this.formErrors[field]) {
        delete this.formErrors[field];
      }
      this.formLevelError = ''; // Clear form level error when user makes changes
    },

    async fetchProduct() {
      try {
        const response = await request({
          method: 'get',
          url: `/products/${this.id}`
        });
        this.formFields = response.data;
      } catch (error) {
        console.error('Error fetching product:', error);
        this.formLevelError = error.response?.data?.message || this.$t('products.fetch_error');
        this.notifyError(this.formLevelError);
        this.$router.push('/admin/products');
      }
    },

    async handleSubmit() {
      try {
        this.validated = true;
        this.isSubmitting = true;
        this.formErrors = {};
        this.formLevelError = '';

        // Validate form fields
        if (!this.formFields.name.trim()) {
          this.formErrors.name = [this.$t('products.validation.name_required')];
          throw new Error('Validation failed');
        }

        await request({
          method: this.isEdit ? 'put' : 'post',
          url: this.isEdit ? `/products/${this.id}` : '/products',
          data: this.formFields
        });

        this.notifySuccess(
          this.isEdit ? this.$t('products.update_success') : this.$t('products.create_success')
        );
        this.$router.push('/admin/products');
      } catch (error) {
        if (error.status === 422) {
          this.formErrors = error.data.message || {};
          this.formLevelError = this.formErrors;
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    cancel() {
      this.$router.push('/admin/products');
    }
  },

  created() {
    if (this.isEdit) {
      this.fetchProduct();
    }
  }
}
</script>

<style scoped>
.product-form {
  padding: 20px;
}
</style>